// TOEFL iBT Mock 2 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.
//
// Mock Test 2 in the PDF has a single Module 2, which is used as `upper`;
// `lower` is a separate, easier set so the adaptive routing is real.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
    {
      id: "m1-words",
      type: "words",
      title: "Social Influence",
      text: "Social influence refers to the way in which individuals change their behavior to meet the demands of a social environment. It takes many forms and can be seen in conformity, socialization, and peer pressure. When [[people]] are [[unsure]] of how to [[act]] in an [[unfamiliar]] [[situation]], they [[often]] [[look]] to [[others]] for [[guidance]]. This [[tendency]] [[helps]] [[maintain]] social [[order]] but may [[also]] [[discourage]] independent thought. Psychologists study these dynamics to understand group behavior better.",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "Campus Event Update",
      text: `Campus Event Update: The International Food Fair will now take place in the Student Center Atrium instead of the outdoor plaza because rain is expected Friday afternoon. The event time is unchanged: 3:00 to 6:00 p.m. Student clubs should arrive by 2:30 p.m. to set up their tables. Visitors do not need tickets, but they are encouraged to bring a reusable container for food samples. A list of participating clubs will be posted on the Student Life website by Thursday evening.`,
      questions: [
        {
          question: "What is the main purpose of the announcement?",
          options: [
            "To cancel the International Food Fair because of rain",
            "To explain a location change for an upcoming campus event",
            "To ask students to buy tickets before Friday",
            "To announce a new Student Life website",
          ],
          correct: 1,
        },
        {
          question: "According to the announcement, when should student clubs arrive?",
          options: [
            "3:00 p.m.",
            "6:00 p.m.",
            "Thursday evening",
            "2:30 p.m.",
          ],
          correct: 3,
        },
        {
          question: "What can be inferred about the outdoor plaza?",
          options: [
            "It is closed for construction all week.",
            "It is too small for student clubs.",
            "It may be affected by bad weather during the event.",
            "It requires visitors to show tickets.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Library Hours Notice",
      text: `Library Notice: Starting Monday, October 20, the Harlow Library will extend its hours for midterm exams. The building will open at 7:00 a.m. and close at midnight, Monday through Thursday. On Friday and Saturday, the regular hours of 9:00 a.m. to 6:00 p.m. will continue.

Group study rooms on the second floor may be reserved for up to two hours at a time through the library website. Food is not allowed in the study rooms, but covered drinks are permitted. Students who wish to borrow a laptop charger or calculator should ask at the front desk before 9:00 p.m., since the desk closes earlier than the building. Extended hours will end on Thursday, November 6.`,
      questions: [
        {
          question: "What is the main purpose of the notice?",
          options: [
            "To announce that the library will close for midterms",
            "To announce temporary longer hours during exam season",
            "To explain how to reserve a laptop",
            "To introduce new rules about food in the building",
          ],
          correct: 1,
        },
        {
          question: "How long can a group study room be reserved at one time?",
          options: [
            "One hour",
            "Thirty minutes",
            "Until midnight",
            "Two hours",
          ],
          correct: 3,
        },
        {
          question: "Why are students told to ask for a charger before 9:00 p.m.?",
          options: [
            "The front desk closes before the library does.",
            "The library runs out of chargers after that time.",
            "Chargers can only be borrowed on weekdays.",
            "The second floor closes at 9:00 p.m.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "The Transition to Sound in Cinema",
      text: `For over three decades, the silent film was the dominant form of cinematic entertainment. Filmmakers relied exclusively on visual techniques, lighting, editing, and exaggerated acting, to convey narrative and emotion. While these films were never truly silent, as they were almost always accompanied by live music, they lacked synchronized dialogue. This reliance on purely visual storytelling allowed cinema to develop a sophisticated, fluid aesthetic distinct from theater.

The release of The Jazz Singer in 1927 marked a pivotal shift. Using the Vitaphone process, the film successfully synchronized recorded audio with the moving image. [A] Although the film contained only a few sequences of spoken dialogue and singing, the public reaction was immediate and overwhelming. [B] Studios that had initially dismissed "talking pictures" as a passing fad were forced to convert their production facilities to sound almost overnight to meet the sudden audience demand.

This transition was not without significant artistic and technical challenges. Early sound recording equipment was extremely sensitive and immobile. [C] To prevent the noise of the camera mechanisms from being recorded, cameras had to be encased in soundproof booths, which severely restricted camera movement. Consequently, early sound films often looked static and stage-bound compared to the dynamic visual style of the late silent masterpieces.

The arrival of sound also disrupted the careers of many silent film stars and altered the global film market. Actors whose voices were unsuited to their public image or who had heavy accents often found themselves out of work. [D] Furthermore, silent films had been a universal medium, easily exported globally by simply changing the text on title cards. The introduction of spoken dialogue created a language barrier that necessitated the development of complex subtitling and dubbing technologies.`,
      questions: [
        {
          question: "According to paragraph 3, why were cameras in early sound films placed inside soundproof booths?",
          options: [
            "To protect the expensive equipment from damage",
            "To prevent the microphone from picking up camera noise",
            "To allow the camera operators to communicate with the director",
            "To improve the lighting conditions for the actors",
          ],
          correct: 1,
        },
        {
          question: "The word \"static\" in the passage is closest in meaning to",
          options: [
            "motionless",
            "complex",
            "noisy",
            "realistic",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred from paragraph 4 about the export of films before the introduction of sound?",
          options: [
            "Films were rarely exported because of high shipping costs.",
            "Global audiences preferred films produced in their own countries.",
            "Actors were required to speak multiple languages to succeed internationally.",
            "It was easier to distribute films internationally during the silent era.",
          ],
          correct: 3,
        },
        {
          question: "Why does the author mention \"The Jazz Singer\" in paragraph 2?",
          options: [
            "To provide an example of a film that failed due to poor audio quality",
            "To argue that musical films were the most popular genre of the 1920s",
            "To identify the specific release that triggered the widespread adoption of sound",
            "To focus on the career of a specific actor who transitioned from silent films",
          ],
          correct: 2,
        },
        {
          question: "Where would the following sentence best fit? \"This technological limitation often forced actors to huddle around hidden microphones, prohibiting natural movement.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 2,
        },
      ],
    },
];

// ── Module 2 ─────────────────────────────────────────────────
const module2 = [
    {
      id: "m2-words",
      type: "words",
      title: "The Spread of Coffee",
      text: "Coffee is one of the most widely consumed drinks in the world. The [[plant]] originally [[grew]] in [[the]] highlands [[of]] Ethiopia, [[and]] its [[seeds]] were [[later]] traded [[across]] the Arabian Peninsula. [[By]] the [[seventeenth]] century, [[coffee]] houses [[had]] spread [[throughout]] Europe, where they became popular places for conversation, business, and the exchange of news. Today, millions of small farmers in tropical countries depend on growing coffee for their income.",
    },
    {
      id: "m2-daily1",
      type: "daily",
      title: "Email to a Professor",
      text: `Subject: Rescheduling my presentation

Dear Professor Alvarez,

I am writing about my group presentation scheduled for Thursday, March 12. I have been chosen to represent the university at a regional debate competition in Denver, and the team's flight leaves that morning. I have already spoken with my group members, Priya and Tom, and they are willing to present on Tuesday, March 17, if that works for you. I would also be glad to present alone on Friday if that is easier.

I have attached the competition letter from my coach. Please let me know which option you prefer by Monday so that I can make arrangements.

Thank you for your understanding.

Best regards,

Jin Park`,
      questions: [
        {
          question: "Why is Jin writing to Professor Alvarez?",
          options: [
            "To report a problem with a group member",
            "To ask for more time to finish a paper",
            "To ask to change the date of a presentation",
            "To invite the professor to a debate competition",
          ],
          correct: 2,
        },
        {
          question: "Why can Jin not present on March 12?",
          options: [
            "Priya and Tom are unavailable.",
            "Jin will be traveling for a competition.",
            "Jin has not finished the slides.",
            "The classroom is closed that day.",
          ],
          correct: 1,
        },
        {
          question: "What does Jin ask the professor to do by Monday?",
          options: [
            "Attend the debate in Denver",
            "Send a letter to the coach",
            "Give the group a higher grade",
            "Choose one of the proposed options",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-daily2",
      type: "daily",
      title: "Parking Lot Memo",
      text: `MEMO
To: All Riverside Office Staff
From: Facilities Management
Date: June 3
Re: Parking Lot B Resurfacing

Parking Lot B will be resurfaced from Monday, June 9, through Wednesday, June 11. The lot will be closed to all vehicles during that time. Employees who normally park there may use Lot C on Dalton Street, which has 60 additional spaces, or the visitor garage, where the daily rate for staff will be reduced to $4. Show your staff badge at the exit gate to receive the reduced rate.

Cars left in Lot B after 6:00 a.m. on June 9 will be towed at the owner's expense. Carpooling is encouraged. Employees who need an accessible space should contact Facilities by June 6.`,
      questions: [
        {
          question: "In the memo, the word \"resurfaced\" is closest in meaning to",
          options: [
            "given a new top layer",
            "painted with new lines",
            "made larger",
            "opened to visitors",
          ],
          correct: 0,
        },
        {
          question: "Which of the following is NOT mentioned as an option for employees during the closure?",
          options: [
            "Using Lot C on Dalton Street",
            "Using the visitor garage at a reduced rate",
            "Leaving cars in Lot B with a special permit",
            "Carpooling with coworkers",
          ],
          correct: 2,
        },
        {
          question: "When will cars still in Lot B be towed?",
          options: [
            "After 6:00 p.m. on June 6",
            "After June 11",
            "After 6:00 a.m. on June 10",
            "After 6:00 a.m. on June 9",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-academic",
      type: "academic",
      title: "Magma Chemistry and Volcano Types",
      text: `Volcanoes differ greatly in shape and in the manner of their eruptions, and much of this variation can be traced to the chemistry of the magma that feeds them. The most important factor is silica content. Magma rich in silica is thick and resistant to flow, a property known as viscosity, whereas magma poor in silica is fluid and moves easily.

Shield volcanoes, such as Mauna Loa in Hawaii, are built almost entirely from fluid basaltic lava. Because this lava travels long distances before it cools and solidifies, it accumulates in broad, gently sloping mountains that resemble a warrior's shield lying on the ground. [A] Eruptions at these volcanoes are usually effusive, meaning that lava pours steadily from vents or fissures rather than being thrown violently into the air. [B] Dissolved gases escape readily from such runny magma, so dangerous explosions are comparatively rare.

Stratovolcanoes, also called composite volcanoes, are steep-sided cones composed of alternating layers of lava, ash, and fragmented rock. Mount Fuji in Japan and Vesuvius in Italy are well-known examples. Their magma contains far more silica than that of shield volcanoes and is correspondingly thick. Gas bubbles trapped within it cannot escape easily, much as bubbles remain trapped in a carbonated drink that has been tightly sealed. [C] When the pressure finally overcomes the strength of the surrounding rock, the result is often a violent explosion.

Pyroclastic flows, fast-moving avalanches of hot gas and volcanic debris, are among the deadliest products of such eruptions. In 79 CE, an eruption of Vesuvius buried the Roman towns of Pompeii and Herculaneum beneath ash and pumice. [D] Volcanologists today monitor ground deformation, gas emissions, and small earthquakes to detect signs that magma is rising. These measurements cannot yet predict the exact timing of an eruption, but they have helped authorities order evacuations that have saved many lives.`,
      questions: [
        {
          question: "The word \"fragmented\" in the passage is closest in meaning to",
          options: [
            "melted by heat",
            "broken into pieces",
            "buried underground",
            "smoothed by erosion",
          ],
          correct: 1,
        },
        {
          question: "According to paragraph 2, why do shield volcanoes have broad, gently sloping shapes?",
          options: [
            "Their eruptions are more explosive than those of other volcanoes.",
            "They are built from layers of ash and fragmented rock.",
            "Their lava flows a long way before it solidifies.",
            "Their magma contains large amounts of silica.",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from paragraph 3 about the violence of volcanic eruptions?",
          options: [
            "It depends partly on how easily gas can leave the magma.",
            "It is determined mainly by the height of the volcano.",
            "It decreases as the amount of silica in magma increases.",
            "It is unrelated to the pressure inside the volcano.",
          ],
          correct: 0,
        },
        {
          question: "Why does the author mention a \"carbonated drink\" in paragraph 3?",
          options: [
            "To suggest that volcanic gases are harmless",
            "To compare the temperature of magma with that of liquids",
            "To explain why shield volcanoes erupt effusively",
            "To illustrate how gas becomes trapped in thick material",
          ],
          correct: 3,
        },
        {
          question: "Where would the following sentence best fit? \"This gentle profile makes the largest shield volcanoes the most massive mountains on Earth.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 0,
        },
      ],
    },
];

// ── Easier Module 2 ──────────────────────────────────────────
const lowerModule2 = [
    {
      id: "low-words",
      type: "words",
      title: "Bicycles",
      text: "Bicycles are a cheap and healthy way to travel. Many [[people]] ride [[them]] to [[work]] or [[school]] every [[day]]. Riding a [[bike]] [[uses]] no [[fuel]], so [[it]] does [[not]] pollute [[the]] air. In some cities, there are special lanes that keep cyclists safe from cars.",
    },
    {
      id: "low-daily1",
      type: "daily",
      title: "Yard Sale Sign",
      text: `YARD SALE
Saturday, May 10 • 9:00 a.m. to 2:00 p.m.
24 Oak Street (the white house on the corner)
Books, toys, kitchen items, and a used bicycle.
Everything under $10!
Cash only, please.`,
      questions: [
        {
          question: "When does the yard sale start?",
          options: ["At 2:00 p.m.", "At 9:00 a.m.", "At 10:00 a.m.", "At noon"],
          correct: 1,
        },
        {
          question: "How can people pay at the yard sale?",
          options: ["By credit card", "By check", "Online", "With cash only"],
          correct: 3,
        },
        {
          question: "What is true about the items for sale?",
          options: [
            "Everything costs less than $10.",
            "Only books are for sale.",
            "The bicycle is new.",
            "The toys are free.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-daily2",
      type: "daily",
      title: "Dentist Appointment Reminder",
      text: `Hello Alex, this is a reminder from Bright Smile Dental. You have an appointment with Dr. Kim on Thursday, June 5, at 3:30 p.m. Please arrive ten minutes early to fill out a short form. If you need to change your appointment, please call us at least one day before. Thank you!`,
      questions: [
        {
          question: "What is the main purpose of the message?",
          options: [
            "To advertise a new dentist",
            "To cancel an appointment",
            "To remind Alex about an appointment",
            "To ask Alex to pay a bill",
          ],
          correct: 2,
        },
        {
          question: "Why should Alex arrive early?",
          options: [
            "To fill out a form",
            "To meet Dr. Kim's assistant",
            "To find parking",
            "To pay for the visit",
          ],
          correct: 0,
        },
        {
          question: "What should Alex do to change the appointment?",
          options: [
            "Send an email on Thursday",
            "Call at least one day before",
            "Visit the office in person",
            "Ask Dr. Kim during the visit",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "low-academic",
      type: "academic",
      title: "The Phases of the Moon",
      text: `When we look at the Moon on different nights, it seems to change shape. Sometimes it is a full, bright circle, and sometimes it is only a thin curve. These different shapes are called the phases of the Moon.

The Moon does not make its own light. Instead, it reflects light from the Sun. [A] At any time, the Sun lights up half of the Moon. As the Moon travels around Earth, we see different amounts of this lit half. [B]

When the Moon is between Earth and the Sun, the lit side faces away from us, so we cannot see it. This is called a new moon. [C] About two weeks later, the Moon is on the other side of Earth, and we can see the whole lit side. This is a full moon.

It takes about twenty-nine and a half days for the Moon to go through all of its phases. [D] For thousands of years, people have used this regular cycle to measure time, and the word "month" comes from the word "moon."`,
      questions: [
        {
          question: "The word \"reflects\" in the passage is closest in meaning to",
          options: ["sends back", "hides", "creates", "heats"],
          correct: 0,
        },
        {
          question: "According to paragraph 2, why does the Moon seem to change shape?",
          options: [
            "The Moon grows and shrinks each month.",
            "Clouds cover parts of the Moon.",
            "We see different amounts of its lit half as it moves around Earth.",
            "The Sun stops shining on the Moon at night.",
          ],
          correct: 2,
        },
        {
          question: "According to paragraph 3, what happens during a new moon?",
          options: [
            "We can see the whole lit side.",
            "The lit side faces away from Earth.",
            "The Moon is on the far side of Earth.",
            "The Moon makes its own light.",
          ],
          correct: 1,
        },
        {
          question: "Why does the author mention the word \"month\" in paragraph 4?",
          options: [
            "To explain why people are afraid of the Moon",
            "To describe how long a year is",
            "To show that the Moon is bigger than it looks",
            "To show how the Moon's cycle has been used to measure time",
          ],
          correct: 3,
        },
        {
          question: "Where would the following sentence best fit? \"In this way, the Moon works like a giant mirror in the sky.\"",
          options: ["[A]", "[B]", "[C]", "[D]"],
          correct: 0,
        },
      ],
    },
];

export const readingModules = {
  module1,
  upper: module2,
  lower: lowerModule2,
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}